import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaChartLine, FaClipboardList, FaFileInvoiceDollar, FaSearchDollar, FaCogs, FaChartBar, FaHandshake, FaEye, FaChessKnight, FaPiggyBank, FaShieldAlt, FaLightbulb } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color:  #dc3545;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#0d6efd"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ControleGestion = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Qu'est-ce que le contrôle de gestion, concrètement ?",
            answer: "Le contrôle de gestion est un processus qui permet de piloter la performance de votre entreprise. Il ne s'agit pas de contrôler au sens strict, mais plutôt de maîtriser votre gestion. Cela implique de définir des objectifs, de mesurer les résultats, d'analyser les écarts par rapport aux prévisions, et de mettre en place des actions correctives. C'est un outil d'aide à la décision stratégique et opérationnelle. Il vous donne une meilleure visibilité sur la rentabilité, l'efficacité et la santé financière de votre entreprise."
        },
        {
            question: "Quelle est la différence entre le contrôle de gestion et la comptabilité ?",
            answer: "La comptabilité se concentre sur l'enregistrement des flux financiers passés, en respectant des normes légales (bilan, compte de résultat). Le contrôle de gestion utilise ces données comptables, mais va plus loin : il les analyse, les projette dans le futur (budget), et les compare à des objectifs pour identifier les leviers d'amélioration de la performance. La comptabilité est une base pour le contrôle de gestion, mais le contrôle de gestion est orienté vers l'action et la prise de décision."
        },
        {
            question: "Le contrôle de gestion est-il uniquement pour les grandes entreprises ?",
            answer: "Absolument pas ! Le contrôle de gestion est essentiel pour les entreprises de toutes tailles, y compris les TPE et PME. Même avec des ressources limitées, il est crucial d'avoir une vision claire de ses coûts, de ses marges et de la rentabilité de ses activités pour assurer la pérennité et la croissance de l'entreprise. Nous adaptons nos outils et notre accompagnement à la taille et aux spécificités de chaque entreprise."
        },
        {
            question: "Pourquoi devrais-je faire appel à CINEF-EC pour mon contrôle de gestion ?",
            answer: "Plusieurs raisons : Expertise : Nous apportons une expertise technique et une expérience diversifiée acquise auprès de nombreuses entreprises. Objectivité : Un regard extérieur permet d'identifier des problèmes ou des opportunités que vous ne verriez peut-être pas de l'intérieur. Gain de temps : Vous vous concentrez sur votre cœur de métier pendant que nous nous occupons de la mise en place et du suivi des outils de pilotage. Coût maîtrisé : L'externalisation est souvent plus économique que l'embauche d'un contrôleur de gestion à temps plein, surtout pour une TPE/PME. Vous bénéficiez de compétences de haut niveau à un coût proportionnel à vos besoins. Flexibilité : Nous adaptons notre intervention à vos besoins évolutifs."
        },
        {
            question: "Ne puis-je pas faire mon contrôle de gestion moi-même avec un tableur Excel ?",
            answer: "C'est possible pour des activités très simples, mais cela devient vite limité et source d'erreurs. Un tableur est un outil, pas une méthodologie. Nous utilisons des outils professionnels (logiciels de reporting, ERP, etc.) et des méthodes éprouvées pour garantir la fiabilité des données, la pertinence des analyses et l'efficacité du pilotage. Nous vous aidons aussi à structurer votre approche et à interpréter les résultats."
        },
        {
            question: "Comment garantissez-vous la confidentialité de mes données ?",
            answer: "La confidentialité est au cœur de notre métier d'expert-comptable. Nous sommes soumis au secret professionnel et mettons en place des mesures de sécurité strictes (accès sécurisés, sauvegarde des données, etc.) pour protéger vos informations. Nous signons un accord de confidentialité détaillé avec chaque client."
        },
        {
            question: "Quels bénéfices concrets puis-je attendre de la mise en place d'un contrôle de gestion ?",
            answer: "Vous pouvez attendre : Meilleure visibilité : Une compréhension claire de la performance de votre entreprise. Prise de décision éclairée : Des décisions basées sur des données fiables et des analyses pertinentes. Amélioration de la rentabilité : Identification des leviers d'optimisation des coûts et des marges. Maîtrise des risques : Anticipation des difficultés et mise en place d'actions préventives. Croissance maîtrisée : Un pilotage efficace de votre développement. Gain de temps : Vous vous concentrez sur ce que vous faites le mieux. Sérénité : Un accompagnement expert pour vous aider à atteindre vos objectifs."
        },
        {
            question: "Proposez-vous des formations pour que mes équipes puissent devenir autonomes ?",
            answer: "Oui, nous proposons des formations sur mesure pour vos équipes, afin de les sensibiliser aux enjeux du contrôle de gestion et de les rendre autonomes sur l'utilisation des outils que nous mettons en place. Nous pouvons également former vos collaborateurs à des outils spécifiques (logiciels de gestion, tableurs, etc.)."
        }
    ];

    const servicesData = [
        {
            icon: <FaChartBar />,
            title: "Tableaux de bord personnalisés",
            description: "Indicateurs clés de performance (KPI) adaptés à votre activité et à vos objectifs, suivis en temps réel pour une vision claire de votre performance.",
            color: "#0d6efd"
        },
        {
            icon: <FaClipboardList />,
            title: "Budget et prévisions",
            description: "Élaboration de budgets prévisionnels, suivi budgétaire, analyse des écarts pour anticiper et maîtriser l'évolution de votre activité.",
            color: "#198754"
        },
        {
            icon: <FaSearchDollar />,
            title: "Calcul et analyse des coûts",
            description: "Détermination de vos coûts de revient, analyse de la rentabilité par produit, service ou activité pour optimiser vos marges.",
            color: "#dc3545"
        },
        {
            icon: <FaCogs />,
            title: "Optimisation des processus",
            description: "Identification des gisements de productivité et des axes d'amélioration de votre efficacité opérationnelle.",
            color: "#6f42c1"
        },
        {
            icon: <FaFileInvoiceDollar />,
            title: "Reporting financier",
            description: "Production de rapports clairs et synthétiques pour faciliter la prise de décision et le pilotage de votre entreprise.",
            color: "#fd7e14"
        },
        {
            icon: <FaChessKnight />,
            title: "Contrôle de gestion externalisé",
            description: "Prise en charge complète ou partielle de votre fonction contrôle de gestion, à un coût maîtrisé et adapté à vos besoins.",
            color: "#20c997"
        }
    ];

    const advantagesData = [
        {
            icon: <FaEye />,
            title: "Vision Claire",
            color: "#0d6efd"
        },
        {
            icon: <FaChessKnight />,
            title: "Décisions Éclairées",
            color: "#198754"
        },
        {
            icon: <FaPiggyBank />,
            title: "Rentabilité Améliorée",
            color: "#dc3545"
        },
        {
            icon: <FaShieldAlt />,
            title: "Risques Anticipés",
            color: "#ffc107"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Contrôle de Gestion</h1>
                            <p className="fs-5 mb-4">Un pilotage efficace pour optimiser la performance de votre entreprise</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Contrôle de Gestion
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                Le contrôle de gestion est bien plus qu'un simple outil de suivi financier. C'est un véritable système de pilotage de votre entreprise.
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                CINEF-EC vous propose un accompagnement personnalisé, adapté à votre taille, à votre secteur d'activité et à vos enjeux spécifiques.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi faire appel à notre cabinet pour votre contrôle de gestion ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaChartLine className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise technique</h5>
                                                <p className="text-muted">
                                                    Nous apportons une expertise technique et une expérience diversifiée acquise auprès de nombreuses entreprises.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaEye className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Regard extérieur</h5>
                                                <p className="text-muted">
                                                    Un regard extérieur permet d'identifier des problèmes ou des opportunités que vous ne verriez peut-être pas de l'intérieur.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Accompagnement sur mesure</h5>
                                                <p className="text-muted">
                                                    Nous adaptons notre intervention à vos besoins évolutifs et vous accompagnons dans l'interprétation des résultats.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaLightbulb className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Solutions innovantes</h5>
                                                <p className="text-muted">
                                                    Nous mettons en place des outils modernes et efficaces, adaptés à votre secteur d'activité et à vos objectifs.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Nos services de contrôle de gestion</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions adaptées à vos besoins spécifiques</p>
                        </div>
                    </div>

                    <div className="row mb-4">
                        {servicesData.slice(0, 3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        {servicesData.slice(3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index + 3}>
                                <ServiceCard delay={`${(index + 3) * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Optimisez la performance de votre entreprise</h3>
                                <p className="text-white fs-5 mb-4">
                                    Le contrôle de gestion est un investissement dans la maîtrise et l'amélioration de votre performance.
                                    Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment nous pouvons vous accompagner.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander une consultation</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Les avantages de nos services</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-3" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur le Contrôle de Gestion</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, #008a44, #dc3545);, 
                      url("/assets/images/image-controle-gestion.png") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default ControleGestion;